// A minimal QR encoder — byte mode, error-correction level M, versions 1–10.
//
// WHY THIS EXISTS RATHER THAN A DEPENDENCY. The TOTP enrolment screen must
// render the `otpauth://` URI as a scannable code IN THE BROWSER: that URI
// carries the staff member's TOTP SECRET, so it may never travel to a QR image
// service, a CDN, or any third party (spec 15 §3.1.3 — the secret is
// KMS-encrypted at rest precisely because it is a credential). That leaves two
// options, a runtime dependency or this file, and the design system's §5
// performance budget says "zero new runtime dependencies" in as many words.
// So the encoder is here, in ~200 lines, and its correctness is not asserted by
// eyeballing a picture: `__tests__/qr.test.ts` compares this matrix BIT FOR BIT
// against the `qrcode` package (already a repo dependency, apps/api uses it for
// the module-02 QR kits) across every version boundary this file supports.
// The oracle is a DEV dependency of apps/web and ships in nothing.
//
// SCOPE, deliberately small: byte mode only (valid for any input, and an
// otpauth URI is mixed-case ASCII where mode optimisation would buy ~nothing),
// level M (the standard default), versions 1–10 → 213 bytes of payload, about
// four times the longest otpauth URI this API can mint. Anything larger throws
// rather than silently truncating; the caller renders the manual-entry path.
//
// Reference: ISO/IEC 18004. The implementation follows the same construction
// order as the oracle (function patterns → reserved format area → data →
// best-mask search including the format bits → final format bits) because a
// different-but-valid mask choice would still scan, and would make the
// bit-exact test a lie.

/** Row-major module matrix; `dark[row * size + col]`. */
export interface QrMatrix {
  readonly size: number;
  readonly dark: readonly boolean[];
  /** 1–10, for the test and for anyone sizing the SVG. */
  readonly version: number;
  /** 0–7, the mask the penalty search chose. */
  readonly mask: number;
}

/** Largest payload this encoder accepts (version 10, level M, byte mode). */
export const QR_MAX_BYTES = 213;

/* -------------------------------------------------------------- tables ---- */

/** Total codewords (data + EC) per version, 1–10. */
const TOTAL_CODEWORDS = [26, 44, 70, 100, 134, 172, 196, 242, 292, 346] as const;
/** EC codewords per block at level M, per version. */
const EC_PER_BLOCK_M = [10, 16, 26, 18, 24, 16, 18, 22, 22, 26] as const;
/** EC blocks at level M, per version. */
const EC_BLOCKS_M = [1, 1, 1, 2, 2, 4, 4, 4, 5, 5] as const;
/** Alignment-pattern centre coordinates per version (empty for version 1). */
const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

const MAX_VERSION = 10;
/** Level M's 2-bit format value (ISO 18004 table 12). */
const EC_LEVEL_BITS_M = 0;

function sizeOf(version: number): number {
  return version * 4 + 17;
}

function dataCodewords(version: number): number {
  return (
    (TOTAL_CODEWORDS[version - 1] ?? 0) -
    (EC_PER_BLOCK_M[version - 1] ?? 0) * (EC_BLOCKS_M[version - 1] ?? 0)
  );
}

/** Byte-mode character-count indicator width (8 bits below version 10). */
function charCountBits(version: number): number {
  return version < 10 ? 8 : 16;
}

/* ------------------------------------------------------- GF(256) + RS ----- */

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if ((x & 0x100) !== 0) x ^= 0x11d; // the QR primitive polynomial
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255] ?? 0;
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[(GF_LOG[a] ?? 0) + (GF_LOG[b] ?? 0)] ?? 0;
}

/** Generator polynomial of degree `degree`, coefficients high→low. */
function generatorPoly(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j] = (next[j] ?? 0) ^ gfMul(poly[j] ?? 0, 1);
      next[j + 1] = (next[j + 1] ?? 0) ^ gfMul(poly[j] ?? 0, GF_EXP[i] ?? 0);
    }
    poly = next;
  }
  return poly;
}

/** Reed–Solomon remainder for one block. */
function ecCodewordsFor(data: Uint8Array, ecLength: number): Uint8Array {
  const gen = generatorPoly(ecLength);
  const remainder = new Uint8Array(ecLength);
  for (const byte of data) {
    const factor = byte ^ (remainder[0] ?? 0);
    remainder.copyWithin(0, 1);
    remainder[ecLength - 1] = 0;
    if (factor !== 0) {
      for (let i = 0; i < ecLength; i++) {
        remainder[i] = (remainder[i] ?? 0) ^ gfMul(gen[i + 1] ?? 0, factor);
      }
    }
  }
  return remainder;
}

/* ------------------------------------------------------------ BCH bits ---- */

function bchDigit(value: number): number {
  let digit = 0;
  let v = value;
  while (v !== 0) {
    digit++;
    v >>>= 1;
  }
  return digit;
}

const G15 = 0b101_0011_0111;
const G15_MASK = 0b101_0100_0001_0010;
const G18 = 0b1_1111_0010_0101;

/** The 15-bit format-information sequence for level M + `mask`. */
function formatBits(mask: number): number {
  const data = (EC_LEVEL_BITS_M << 3) | mask;
  let d = data << 10;
  while (bchDigit(d) - bchDigit(G15) >= 0) {
    d ^= G15 << (bchDigit(d) - bchDigit(G15));
  }
  return ((data << 10) | d) ^ G15_MASK;
}

/** The 18-bit version-information sequence (versions 7 and up). */
function versionBits(version: number): number {
  let d = version << 12;
  while (bchDigit(d) - bchDigit(G18) >= 0) {
    d ^= G18 << (bchDigit(d) - bchDigit(G18));
  }
  return (version << 12) | d;
}

/* ------------------------------------------------------------- bitmap ----- */

class Modules {
  readonly size: number;
  readonly dark: Uint8Array;
  readonly reserved: Uint8Array;

  constructor(size: number) {
    this.size = size;
    this.dark = new Uint8Array(size * size);
    this.reserved = new Uint8Array(size * size);
  }

  set(row: number, col: number, dark: boolean, reserve = false): void {
    this.dark[row * this.size + col] = dark ? 1 : 0;
    if (reserve) this.reserved[row * this.size + col] = 1;
  }

  get(row: number, col: number): number {
    return this.dark[row * this.size + col] ?? 0;
  }

  isReserved(row: number, col: number): boolean {
    return this.reserved[row * this.size + col] === 1;
  }

  xor(row: number, col: number, on: boolean): void {
    if (!on) return;
    // Read-modify-write through a local: under `noUncheckedIndexedAccess` a
    // typed-array element is `number | undefined`, so `^= 1` does not compile.
    const at = row * this.size + col;
    this.dark[at] = (this.dark[at] ?? 0) ^ 1;
  }
}

/* ------------------------------------------------------ function areas ---- */

function placeFinderPatterns(m: Modules): void {
  const positions: readonly (readonly [number, number])[] = [
    [0, 0],
    [0, m.size - 7],
    [m.size - 7, 0],
  ];
  for (const [row, col] of positions) {
    for (let r = -1; r <= 7; r++) {
      if (row + r < 0 || row + r >= m.size) continue;
      for (let c = -1; c <= 7; c++) {
        if (col + c < 0 || col + c >= m.size) continue;
        const onRing = r >= 0 && r <= 6 && (c === 0 || c === 6);
        const onBar = c >= 0 && c <= 6 && (r === 0 || r === 6);
        const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        m.set(row + r, col + c, onRing || onBar || inCore, true);
      }
    }
  }
}

function placeTimingPatterns(m: Modules): void {
  for (let i = 8; i < m.size - 8; i++) {
    const dark = i % 2 === 0;
    m.set(i, 6, dark, true);
    m.set(6, i, dark, true);
  }
}

function placeAlignmentPatterns(m: Modules, version: number): void {
  const centers = ALIGNMENT_CENTERS[version - 1] ?? [];
  const last = centers.length - 1;
  for (let i = 0; i < centers.length; i++) {
    for (let j = 0; j < centers.length; j++) {
      // The three finder corners own their area — and ONLY those three. The
      // centre on the timing row (v7's (6,22)) is a real alignment pattern and
      // deliberately overwrites the timing modules it lands on, so "is this
      // already reserved?" is the wrong exclusion test.
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) continue;
      const row = centers[i] ?? 0;
      const col = centers[j] ?? 0;
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          const ring = r === -2 || r === 2 || c === -2 || c === 2;
          m.set(row + r, col + c, ring || (r === 0 && c === 0), true);
        }
      }
    }
  }
}

/** Format bits (and the fixed dark module), reserved even while provisional. */
function placeFormatInfo(m: Modules, mask: number): void {
  const bits = formatBits(mask);
  for (let i = 0; i < 15; i++) {
    const dark = ((bits >> i) & 1) === 1;
    if (i < 6) m.set(i, 8, dark, true);
    else if (i < 8) m.set(i + 1, 8, dark, true);
    else m.set(m.size - 15 + i, 8, dark, true);

    if (i < 8) m.set(8, m.size - i - 1, dark, true);
    else if (i < 9) m.set(8, 15 - i, dark, true);
    else m.set(8, 14 - i, dark, true);
  }
  m.set(m.size - 8, 8, true, true);
}

function placeVersionInfo(m: Modules, version: number): void {
  const bits = versionBits(version);
  for (let i = 0; i < 18; i++) {
    const row = Math.floor(i / 3);
    const col = (i % 3) + m.size - 11;
    const dark = ((bits >> i) & 1) === 1;
    m.set(row, col, dark, true);
    m.set(col, row, dark, true);
  }
}

/* --------------------------------------------------------- data stream ---- */

function encodeCodewords(bytes: Uint8Array, version: number): Uint8Array {
  const capacityBits = dataCodewords(version) * 8;
  const bits: number[] = [];
  const push = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(bytes.length, charCountBits(version));
  for (const byte of bytes) push(byte, 8);

  if (bits.length + 4 <= capacityBits) push(0, 4); // terminator
  while (bits.length % 8 !== 0) bits.push(0);

  const buffer = new Uint8Array(capacityBits / 8);
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let b = 0; b < 8; b++) byte = (byte << 1) | (bits[i + b] ?? 0);
    buffer[i / 8] = byte;
  }
  // Pad codewords, alternating from 0xEC, per ISO 18004 §8.4.9.
  let pad = 0;
  for (let i = bits.length / 8; i < buffer.length; i++) {
    buffer[i] = pad % 2 === 0 ? 0xec : 0x11;
    pad++;
  }

  return interleave(buffer, version);
}

/** Split into blocks, RS-encode each, interleave data then EC (§8.6). */
function interleave(buffer: Uint8Array, version: number): Uint8Array {
  const total = TOTAL_CODEWORDS[version - 1] ?? 0;
  const blocks = EC_BLOCKS_M[version - 1] ?? 1;
  const ecLength = EC_PER_BLOCK_M[version - 1] ?? 0;
  const dataTotal = dataCodewords(version);

  const blocksInGroup2 = total % blocks;
  const blocksInGroup1 = blocks - blocksInGroup2;
  const dataInGroup1 = Math.floor(dataTotal / blocks);

  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;
  let longest = 0;
  for (let b = 0; b < blocks; b++) {
    const size = b < blocksInGroup1 ? dataInGroup1 : dataInGroup1 + 1;
    const block = buffer.slice(offset, offset + size);
    dataBlocks.push(block);
    ecBlocks.push(ecCodewordsFor(block, ecLength));
    offset += size;
    longest = Math.max(longest, size);
  }

  const out = new Uint8Array(total);
  let index = 0;
  for (let i = 0; i < longest; i++) {
    for (const block of dataBlocks) {
      if (i < block.length) out[index++] = block[i] ?? 0;
    }
  }
  for (let i = 0; i < ecLength; i++) {
    for (const block of ecBlocks) out[index++] = block[i] ?? 0;
  }
  return out;
}

/** Zigzag placement, two columns at a time, skipping the timing column. */
function placeData(m: Modules, codewords: Uint8Array): void {
  let inc = -1;
  let row = m.size - 1;
  let bitIndex = 7;
  let byteIndex = 0;

  for (let col = m.size - 1; col > 0; col -= 2) {
    if (col === 6) col--;
    for (;;) {
      for (let c = 0; c < 2; c++) {
        if (m.isReserved(row, col - c)) continue;
        let dark = false;
        if (byteIndex < codewords.length) {
          dark = (((codewords[byteIndex] ?? 0) >>> bitIndex) & 1) === 1;
        }
        m.set(row, col - c, dark);
        bitIndex--;
        if (bitIndex === -1) {
          byteIndex++;
          bitIndex = 7;
        }
      }
      row += inc;
      if (row < 0 || row >= m.size) {
        row -= inc;
        inc = -inc;
        break;
      }
    }
  }
}

/* -------------------------------------------------------------- masking --- */

function maskAt(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      return (((row * col) % 3) + ((row + col) % 2)) % 2 === 0;
  }
}

function applyMask(m: Modules, mask: number): void {
  for (let row = 0; row < m.size; row++) {
    for (let col = 0; col < m.size; col++) {
      if (m.isReserved(row, col)) continue;
      m.xor(row, col, maskAt(mask, row, col));
    }
  }
}

/** ISO 18004 §8.8.2 feature penalties, N1=3 N2=3 N3=40 N4=10. */
function penalty(m: Modules): number {
  const size = m.size;
  let points = 0;

  // N1 — runs of five or more same-coloured modules, both directions.
  for (let a = 0; a < size; a++) {
    let runRow = 0;
    let runCol = 0;
    let lastRow = -1;
    let lastCol = -1;
    for (let b = 0; b < size; b++) {
      const inRow = m.get(a, b);
      if (inRow === lastRow) runRow++;
      else {
        if (runRow >= 5) points += 3 + (runRow - 5);
        lastRow = inRow;
        runRow = 1;
      }
      const inCol = m.get(b, a);
      if (inCol === lastCol) runCol++;
      else {
        if (runCol >= 5) points += 3 + (runCol - 5);
        lastCol = inCol;
        runCol = 1;
      }
    }
    if (runRow >= 5) points += 3 + (runRow - 5);
    if (runCol >= 5) points += 3 + (runCol - 5);
  }

  // N2 — 2×2 blocks of one colour.
  let blocks = 0;
  for (let row = 0; row < size - 1; row++) {
    for (let col = 0; col < size - 1; col++) {
      const sum =
        m.get(row, col) + m.get(row, col + 1) + m.get(row + 1, col) + m.get(row + 1, col + 1);
      if (sum === 0 || sum === 4) blocks++;
    }
  }
  points += blocks * 3;

  // N3 — the 1:1:3:1:1 finder-lookalike with its four-module light margin.
  let lookalikes = 0;
  for (let a = 0; a < size; a++) {
    let windowRow = 0;
    let windowCol = 0;
    for (let b = 0; b < size; b++) {
      windowRow = ((windowRow << 1) & 0x7ff) | m.get(a, b);
      if (b >= 10 && (windowRow === 0x5d0 || windowRow === 0x05d)) lookalikes++;
      windowCol = ((windowCol << 1) & 0x7ff) | m.get(b, a);
      if (b >= 10 && (windowCol === 0x5d0 || windowCol === 0x05d)) lookalikes++;
    }
  }
  points += lookalikes * 40;

  // N4 — deviation of the dark ratio from 50%, in 5% steps.
  let dark = 0;
  for (const bit of m.dark) dark += bit;
  const k = Math.abs(Math.ceil((dark * 100) / m.dark.length / 5) - 10);
  points += k * 10;

  return points;
}

/* ----------------------------------------------------------------- API ---- */

function smallestVersion(byteLength: number): number {
  for (let version = 1; version <= MAX_VERSION; version++) {
    const headerBits = 4 + charCountBits(version);
    if (headerBits + byteLength * 8 <= dataCodewords(version) * 8) return version;
  }
  throw new Error(
    `qr: ${String(byteLength)} bytes exceeds the ${String(QR_MAX_BYTES)}-byte capacity of this encoder`,
  );
}

/**
 * Encode `text` as a QR symbol (byte mode, level M).
 *
 * Throws when the payload is larger than version 10 at level M can carry — the
 * caller renders the manual-entry path rather than a code that cannot be read.
 */
export function encodeQr(text: string): QrMatrix {
  const bytes = new TextEncoder().encode(text);
  if (bytes.length === 0) throw new Error("qr: nothing to encode");
  const version = smallestVersion(bytes.length);

  const m = new Modules(sizeOf(version));
  placeFinderPatterns(m);
  placeTimingPatterns(m);
  placeAlignmentPatterns(m, version);
  // Provisional format bits: written so the area is RESERVED before masking,
  // replaced with the chosen mask's bits at the end.
  placeFormatInfo(m, 0);
  if (version >= 7) placeVersionInfo(m, version);
  placeData(m, encodeCodewords(bytes, version));

  let bestMask = 0;
  let bestPenalty = Number.POSITIVE_INFINITY;
  for (let mask = 0; mask < 8; mask++) {
    placeFormatInfo(m, mask);
    applyMask(m, mask);
    const score = penalty(m);
    applyMask(m, mask); // undo — the mask is its own inverse
    if (score < bestPenalty) {
      bestPenalty = score;
      bestMask = mask;
    }
  }

  applyMask(m, bestMask);
  placeFormatInfo(m, bestMask);

  return {
    size: m.size,
    dark: Array.from(m.dark, (bit) => bit === 1),
    version,
    mask: bestMask,
  };
}
